import React, { FC, useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { UserObjectType, UserShortSchema } from 'constants/graphqlTypes';
import {
  useGetSparkCategories,
  useGetSparksGradients,
  useCreateSpark,
  ISparkCategory,
  ISparkCoverMedia,
} from 'graphQL/sparks/hooks';
import LoaderContent from 'components/UI/LoaderContent';
import Grain from 'components/UI/Grain';
import { getUserInfo } from 'components/Profile/ProfileTop/helpers';
import getAuthUser from 'helpers/getAuthUser';
import { useGetUser } from 'graphQL/profile/hooks';

import sparkCoverExample from 'assets/images/spark-cover-example.png';

import {
  StyledModalOverlay,
  StyledModalContainer,
  StyledModalHeader,
  StyledViewControls,
  StyledCancelButton,
  StyledHeaderTitle,
  StyledTitle,
  StyledModalContent,
  StyledCardsGrid,
  StyledSparkCard,
  StyledCardCover,
  StyledLoadingContainer,
  StyledStyleViewControls,
  StyledCreateButton,
  StyledPreviewContainer,
  StyledPreviewCard,
  StyledStyleSection,
  StyledStyleHeader,
  StyledStyleLabel,
  StyledStyleCarousel,
  StyledStyleTile,
  StyledPreviewVideo,
  StyledThoughtInputSection,
  StyledThoughtInputBorder,
  StyledThoughtInputRow,
  StyledThoughtAvatar,
  StyledThoughtInput,
  StyledSlideContainer,
  StyledSlidePanel,
  StyledIntroContainer,
  StyledIntroCard,
  StyledIntroCardNoise,
  StyledIntroCardTitle,
  StyledIntroCardImage,
  StyledIntroTextSection,
  StyledIntroHeading,
  StyledIntroDescription,
  StyledIntroNextButton,
} from './styled';

interface ISparkSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional category slug to skip category selection and go directly to style selection */
  initialCategorySlug?: string;
  /** Show intro step explaining what sparks are (for first-time users) */
  showIntro?: boolean;
}

const SparkSelectionModal: FC<ISparkSelectionModalProps> = ({
  isOpen,
  onClose,
  initialCategorySlug,
  showIntro = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  // When showIntro is true: 0 = intro, 1 = category selection, 2 = style selection
  // When showIntro is false: 0 = category selection, 1 = style selection
  const [step, setStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<ISparkCategory | null>(null);
  const [selectedCoverMedia, setSelectedCoverMedia] = useState<ISparkCoverMedia | null>(null);
  const [thought, setThought] = useState('');

  // Fetch spark categories with their cover media
  const { items: categories, loading } = useGetSparkCategories({ limit: 20 });

  // Fetch gradient options (for mapping cover media to gradients)
  const { items: gradients } = useGetSparksGradients();

  // Get current user for avatar
  const { user: currentUser } = useGetUser(getAuthUser().userId);

  // Find a matching gradient for the selected cover media
  const selectedGradient = useMemo(() => {
    if (!selectedCoverMedia || !gradients.length) return gradients[0];
    // For now, use the first gradient as default
    // In the future, cover media could have a gradient association
    return gradients[0];
  }, [selectedCoverMedia, gradients]);

  const handleClose = () => {
    setStep(0);
    setSelectedCategory(null);
    setSelectedCoverMedia(null);
    setThought('');
    onClose();
  };

  const { handleDone: createSpark, loading: creating } = useCreateSpark({
    title: selectedCategory?.name ?? '',
    bgColorId: selectedGradient?.colorId ?? '',
    isPrivate: true,
    coverMediaId: selectedCoverMedia?.id,
    categorySlug: selectedCategory?.slug ?? undefined,
    description: thought || undefined,
    onCompleted: () => {
      handleClose();
    },
  });

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
      document.body.style.overflow = 'hidden';
    } else {
      setIsVisible(false);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Calculate the step offsets based on showIntro
  const categoryStep = showIntro ? 1 : 0;
  const styleStep = showIntro ? 2 : 1;

  // Reset state when modal opens, auto-select category if slug provided
  useEffect(() => {
    if (isOpen) {
      setThought('');

      // If initialCategorySlug is provided and categories are loaded, auto-select and skip to style step
      if (initialCategorySlug && categories.length > 0) {
        const matchingCategory = categories.find((cat) => cat.slug === initialCategorySlug);
        if (matchingCategory) {
          setSelectedCategory(matchingCategory);
          setSelectedCoverMedia(matchingCategory.coverMedia[0] ?? null);
          setStep(styleStep);
          return;
        }
      }

      // Otherwise, start at the first step (intro if showIntro, otherwise category selection)
      setStep(0);
      setSelectedCategory(null);
      setSelectedCoverMedia(null);
    }
  }, [isOpen, initialCategorySlug, categories, styleStep]);

  // Set default cover media when category is selected
  useEffect(() => {
    if (selectedCategory && selectedCategory.coverMedia.length > 0 && !selectedCoverMedia) {
      setSelectedCoverMedia(selectedCategory.coverMedia[0]);
    }
  }, [selectedCategory, selectedCoverMedia]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleSelectCategory = (category: ISparkCategory) => {
    setSelectedCategory(category);
    setSelectedCoverMedia(category.coverMedia[0] ?? null);
    setStep(styleStep);
  };

  const handleIntroNext = () => {
    setStep(categoryStep);
  };

  const handleBack = () => {
    // If we auto-skipped due to initialCategorySlug, close the modal instead of going back
    if (initialCategorySlug) {
      handleClose();
      return;
    }
    // From style selection, go back to category selection
    if (step === styleStep) {
      setStep(categoryStep);
      setSelectedCategory(null);
      setSelectedCoverMedia(null);
    } else if (step === categoryStep && showIntro) {
      // From category selection, go back to intro if showIntro is true
      setStep(0);
    } else {
      // Otherwise close
      handleClose();
    }
  };

  const handleCreate = () => {
    if (selectedCategory && selectedCoverMedia) {
      createSpark();
    }
  };

  const getAvatarUrl = (user?: UserObjectType | UserShortSchema | null): string | undefined => {
    if (!user) return undefined;
    const { avatar } = getUserInfo(user);
    return avatar ?? undefined;
  };

  const getMediaPreviewUrl = (media: ISparkCoverMedia | null): string => {
    return media?.mediaUrl ?? '';
  };

  const isAnimatedMedia = (media: ISparkCoverMedia | null): boolean => {
    return media?.mediaType === 'ANIMATION';
  };

  // Get the first cover media image for category preview
  // Use thumbnailUrl for animated media (videos) to show a static preview
  const getCategoryPreviewImage = (category: ISparkCategory): string => {
    const firstMedia = category.coverMedia[0];
    if (!firstMedia) return '';
    // For animated media, prefer thumbnailUrl (first frame), fall back to mediaUrl
    if (firstMedia.mediaType === 'ANIMATION') {
      return firstMedia.thumbnailUrl || firstMedia.mediaUrl || '';
    }
    return firstMedia.mediaUrl ?? '';
  };

  if (!isOpen) return null;

  const panelCount = showIntro ? 3 : 2;

  const modalContent = (
    <StyledModalOverlay isOpen={isVisible} onClick={handleOverlayClick}>
      <StyledModalContainer isOpen={isVisible} onClick={(e) => e.stopPropagation()}>
        <StyledSlideContainer step={step} panelCount={panelCount}>
          {/* Intro Step (only when showIntro is true) */}
          {showIntro && (
            <StyledSlidePanel panelCount={panelCount}>
              <StyledModalHeader>
                <StyledViewControls>
                  <StyledCancelButton onClick={handleClose}>Cancel</StyledCancelButton>
                </StyledViewControls>
              </StyledModalHeader>

              <StyledIntroContainer>
                <StyledIntroCard>
                  <StyledIntroCardNoise />
                  <StyledIntroCardTitle>What is a Spark?</StyledIntroCardTitle>
                  <StyledIntroCardImage src={sparkCoverExample} alt="Spark example" />
                </StyledIntroCard>

                <StyledIntroTextSection>
                  <StyledIntroHeading>How Sparks work</StyledIntroHeading>
                  <StyledIntroDescription>
                    Think of it as a special type of list — a list template. Each spark has a theme. You add in your
                    curations. Designed to spark up fun &amp; discovery with friends.
                  </StyledIntroDescription>
                </StyledIntroTextSection>
              </StyledIntroContainer>

              <StyledIntroNextButton onClick={handleIntroNext}>Browse Sparks</StyledIntroNextButton>
            </StyledSlidePanel>
          )}

          {/* Category Selection Step */}
          <StyledSlidePanel panelCount={panelCount}>
            <StyledModalHeader>
              <StyledViewControls>
                <StyledCancelButton onClick={showIntro ? handleBack : handleClose}>Cancel</StyledCancelButton>
              </StyledViewControls>
              <StyledHeaderTitle>
                <StyledTitle>Select the question to begin</StyledTitle>
              </StyledHeaderTitle>
            </StyledModalHeader>

            <StyledModalContent>
              {loading ? (
                <StyledLoadingContainer>
                  <LoaderContent isCentered isDark />
                </StyledLoadingContainer>
              ) : (
                <StyledCardsGrid>
                  {categories.map((category) => {
                    const previewImage = getCategoryPreviewImage(category);

                    return (
                      <StyledSparkCard key={category.id} onClick={() => handleSelectCategory(category)}>
                        <StyledCardCover bgImage={previewImage}>
                          <Grain />
                        </StyledCardCover>
                      </StyledSparkCard>
                    );
                  })}
                </StyledCardsGrid>
              )}
            </StyledModalContent>
          </StyledSlidePanel>

          {/* Style Selection Step */}
          <StyledSlidePanel panelCount={panelCount}>
            <StyledModalHeader>
              <StyledStyleViewControls>
                <StyledCancelButton onClick={handleBack}>Cancel</StyledCancelButton>
                <StyledCreateButton onClick={handleCreate} disabled={!selectedCoverMedia || creating}>
                  {creating ? 'Creating...' : 'Create'}
                </StyledCreateButton>
              </StyledStyleViewControls>
            </StyledModalHeader>

            <StyledPreviewContainer>
              <StyledPreviewCard
                bgImage={isAnimatedMedia(selectedCoverMedia) ? undefined : getMediaPreviewUrl(selectedCoverMedia)}
              >
                {isAnimatedMedia(selectedCoverMedia) && selectedCoverMedia?.mediaUrl && (
                  <StyledPreviewVideo src={selectedCoverMedia.mediaUrl} autoPlay loop muted playsInline />
                )}
              </StyledPreviewCard>
            </StyledPreviewContainer>

            <StyledStyleSection>
              <StyledStyleHeader>
                <StyledStyleLabel>Select your style</StyledStyleLabel>
              </StyledStyleHeader>
              <StyledStyleCarousel>
                {selectedCategory?.coverMedia.map((media) => {
                  const isSelected = media.id === selectedCoverMedia?.id;
                  const isAnimated = media.mediaType === 'ANIMATION';
                  // For animated media, use thumbnailUrl as static preview in tiles
                  const thumbnailImage = isAnimated ? media.thumbnailUrl || media.mediaUrl || '' : media.mediaUrl ?? '';

                  return (
                    <StyledStyleTile
                      key={media.id}
                      bgImage={thumbnailImage}
                      isSelected={isSelected}
                      onClick={() => setSelectedCoverMedia(media)}
                    />
                  );
                })}
              </StyledStyleCarousel>
            </StyledStyleSection>

            <StyledThoughtInputSection>
              <StyledThoughtInputBorder />
              <StyledThoughtInputRow>
                <StyledThoughtAvatar src={getAvatarUrl(currentUser)} />
                <StyledThoughtInput
                  type="text"
                  placeholder="Add your thought"
                  value={thought}
                  onChange={(e) => setThought(e.target.value)}
                />
              </StyledThoughtInputRow>
            </StyledThoughtInputSection>
          </StyledSlidePanel>
        </StyledSlideContainer>
      </StyledModalContainer>
    </StyledModalOverlay>
  );

  return createPortal(modalContent, document.body);
};

export default SparkSelectionModal;
